import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import L from "leaflet"
import { MapFactory } from "./map/map_factory.js"
import { MapRenderer } from "./map/map_renderer.js"
import { DataFetcher } from "./map/data_fetcher.js"
import { MapUtils } from "./map/map_utils.js"
import { HuntingAreasRenderer } from "./map/hunting_areas_renderer.js"
import { KilledGameRenderer } from "./map/killed_game_renderer.js"

export default class extends Controller {
  static targets = ["container"]
  static values = {
    amenityUrl: String,
    personalUrl: String,
    seenUrl: String,
    huntingAreasUrl: String,
    killedGameUrl: String,
    layerType: String,
    activeBaseLayer: String,
    killedGame: Array,
    huntingLogId: Number,
    currentUserId: Number
  }

  connect() {
    this.initializeComponents();
    this.boundHandleStreamRender = this.handleStreamRender.bind(this);
    document.addEventListener('turbo:before-stream-render', this.boundHandleStreamRender);

    setTimeout(() => {
      if (this.element.dataset.geolocation) {
        this.drawMainMap();
      } else if (this.element.dataset.location) {
        this.previewLocation();
      }
    }, 500); // Delay to ensure the map container is ready
  }

  initializeComponents() {
    this.renderer = null;
    this.dataFetcher = null;
    this.debouncedLoad = null;
    this.huntingAreasRenderer = null;
    this.killedGameRenderer = null;
    this.sessionSubscription = null;
    this.hunterMarkers = {};
    this.contextMenu = null;
    this.boundHideContextMenu = null;
  }

  disconnect() {
    document.removeEventListener('turbo:before-stream-render', this.boundHandleStreamRender);
    if (this.boundHideContextMenu) {
      document.removeEventListener('click', this.boundHideContextMenu);
    }
    if (this.contextMenu) {
      this.contextMenu.remove();
      this.contextMenu = null;
    }
    if (this.sessionSubscription) {
      this.sessionSubscription.unsubscribe();
      this.sessionSubscription = null;
    }
    clearTimeout(this.mapRefreshTimer);
    if (this.mapSubscription) {
      this.mapSubscription.unsubscribe();
      this.mapSubscription = null;
    }
  }

  handleStreamRender(event) {
    if (!this.renderer || !this.dataFetcher) return;
    const streamEl = event.detail.newStream;
    if (!streamEl) return;

    const action = streamEl.getAttribute('action');
    const target = streamEl.getAttribute('target');

    if (action === 'remove') {
      if (target && target.startsWith('personal_location_')) {
        this.renderer.removeMarker(target);
        this.dataFetcher.lastFetchBoundsPersonal = null;
      } else if (target && target.startsWith('personal_seen_')) {
        // Marker IDs use "seen_N" but dom_id produces "personal_seen_N"
        this.renderer.removeMarker(target.replace('personal_', ''));
        this.dataFetcher.lastFetchBoundsSeen = null;
      }
    } else if (action === 'prepend') {
      if (target === 'locations') {
        this.dataFetcher.lastFetchBoundsPersonal = null;
        this.drawPersonalData();
      } else if (target === 'seens') {
        this.dataFetcher.lastFetchBoundsSeen = null;
        this.drawSeenData();
      }
    }
  }

  previewLocation() {
    const center = JSON.parse(this.element.dataset.location);
    this.map = MapFactory.createPreviewMap(this.containerTarget, center);
    
    this.renderer = new MapRenderer(this.map);
    this.renderer.renderPreviewLocation(center);
  }

  drawMainMap() {
    const center = JSON.parse(this.element.dataset.geolocation);
    const options = {
      wheelZoom: this.element.dataset.wheelZoom,
      layerType: this.layerTypeValue,
      activeBaseLayer: this.activeBaseLayerValue
    };
    
    this.map = MapFactory.createMainMap(this.containerTarget, center, options);
    
    // Initialize components
    this.renderer = new MapRenderer(this.map);
    this.dataFetcher = new DataFetcher(this.map);
    this.huntingAreasRenderer = new HuntingAreasRenderer(this.map, this.dataFetcher);
    this.killedGameRenderer = new KilledGameRenderer(this.map);
    
    // Render killed game if data is provided
    if (this.hasKilledGameValue && this.killedGameValue.length > 0) {
      this.killedGameRenderer.render(this.killedGameValue);
    }
    
    // Setup data fetching if URLs are provided
    this.setupDataFetching();

    // Right-click context menu
    this.setupContextMenu();

    // Live updates: subscribe to the active hunt session for real-time
    // hunter positions and killed game (no manual reload needed).
    if (this.hasHuntingLogIdValue && this.huntingLogIdValue > 0) {
      this.connectHuntingSession();
    }

    // Live updates for sightings, locations and amenities shared with this user
    this.connectMapChannel();
  }

  // --- Live map changes (ActionCable) ---

  connectMapChannel() {
    this.mapConsumer ||= createConsumer();
    this.mapSubscription = this.mapConsumer.subscriptions.create(
      { channel: "MapChannel" },
      { received: (data) => this.handleMapChange(data) }
    );
  }

  handleMapChange(data) {
    if (data.type !== "map_changed" || !this.renderer || !this.dataFetcher) return;

    // Gone for this user (deleted, or turned private): drop the marker right away
    if (data.action === "destroyed" && data.marker_id) this.renderer.removeMarker(data.marker_id);

    // A burst of changes becomes one refetch
    this.pendingMapKinds ||= new Set();
    this.pendingMapKinds.add(data.kind);
    clearTimeout(this.mapRefreshTimer);
    this.mapRefreshTimer = setTimeout(() => this.refreshChangedMapKinds(), 700);
  }

  refreshChangedMapKinds() {
    const kinds = this.pendingMapKinds || new Set();
    this.pendingMapKinds = new Set();

    // Clearing the "last fetched" bounds makes the next draw ask the server again
    if (kinds.has("sighting") && this.seenUrlValue) {
      this.dataFetcher.lastFetchBoundsSeen = null;
      this.drawSeenData();
    }
    if (kinds.has("location") && this.personalUrlValue) {
      this.dataFetcher.lastFetchBoundsPersonal = null;
      this.drawPersonalData();
    }
    if (kinds.has("amenity") && this.amenityUrlValue) {
      this.dataFetcher.lastFetchBoundsHunting = null;
      this.drawHuntingData();
    }
  }

  // --- Live hunt session (ActionCable) ---

  connectHuntingSession() {
    const consumer = createConsumer();
    this.sessionSubscription = consumer.subscriptions.create(
      { channel: "HuntingSessionChannel", hunting_log_id: this.huntingLogIdValue },
      { received: (data) => this.handleSessionMessage(data) }
    );
  }

  handleSessionMessage(data) {
    if (data.type === "location_update") {
      this.updateHunterMarker(data);
    } else if (data.type === "participant_left") {
      this.removeHunterMarker(data.user_id);
    } else if (data.type === "killed_game") {
      this.addLiveKilledGame(data);
    }
  }

  updateHunterMarker({ user_id, name, latitude, longitude }) {
    if (!this.map) return;

    const isSelf = user_id === this.currentUserIdValue;
    const color = isSelf ? "#206bc4" : "#e67e22";
    const initial = (name || "?")[0].toUpperCase();

    if (this.hunterMarkers[user_id]) {
      this.hunterMarkers[user_id].setLatLng([latitude, longitude]);
      return;
    }

    const icon = L.divIcon({
      html: `<div style="width:32px;height:32px;border-radius:50%;background:${color};color:#fff;font-weight:bold;font-size:14px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${initial}</div>`,
      className: "",
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });

    this.hunterMarkers[user_id] = L.marker([latitude, longitude], { icon })
      .bindTooltip(name, { permanent: false, direction: "top" })
      .addTo(this.map);
  }

  removeHunterMarker(userId) {
    if (this.hunterMarkers[userId]) {
      this.hunterMarkers[userId].remove();
      delete this.hunterMarkers[userId];
    }
  }

  addLiveKilledGame({ game_type, quantity, latitude, longitude }) {
    if (!this.killedGameRenderer) return;

    // Respect the toggle: only draw if killed game layer is visible.
    const toggle = document.getElementById("todayKilledGameToggle");
    if (toggle && !toggle.checked) return;

    this.killedGameRenderer.addMarker({
      lat: latitude,
      lng: longitude,
      type: (game_type || "").replace(/_/g, " "),
      quantity: quantity,
      collected: false,
      can_edit: false
    });
  }

  setupContextMenu() {
    this.contextMenu = document.createElement('ul');
    this.contextMenu.className = 'dropdown-menu';
    this.contextMenu.style.cssText = 'position:absolute;z-index:2000;display:none;';
    this.containerTarget.appendChild(this.contextMenu);

    this.map.on('contextmenu', (e) => {
      e.originalEvent.preventDefault();
      this.showContextMenu(e.containerPoint, e.latlng);
    });

    this.map.on('click', () => this.hideContextMenu());
    this.map.on('movestart', () => this.hideContextMenu());

    this.boundHideContextMenu = () => this.hideContextMenu();
    document.addEventListener('click', this.boundHideContextMenu);
  }

  showContextMenu(point, latlng) {
    const lat = latlng.lat.toFixed(6);
    const lng = latlng.lng.toFixed(6);

    const sourceMenu = document.querySelector('.map-action-dropdown .dropdown-menu');
    if (!sourceMenu) return;

    this.contextMenu.innerHTML = '';

    sourceMenu.querySelectorAll('li').forEach(li => {
      const clone = li.cloneNode(true);
      const anchor = clone.querySelector('a.dropdown-item');
      if (anchor) {
        try {
          const url = new URL(anchor.href);
          url.searchParams.set('latitude', lat);
          url.searchParams.set('longitude', lng);
          anchor.href = url.toString();
        } catch (_) {}
      }
      this.contextMenu.appendChild(clone);
    });

    const mapWidth = this.containerTarget.offsetWidth;
    const mapHeight = this.containerTarget.offsetHeight;
    const menuWidth = 200;
    const menuHeight = 360;

    const x = (point.x + menuWidth > mapWidth) ? point.x - menuWidth : point.x;
    const y = (point.y + menuHeight > mapHeight) ? point.y - menuHeight : point.y;

    this.contextMenu.style.left = `${x}px`;
    this.contextMenu.style.top = `${y}px`;
    this.contextMenu.style.display = 'block';
  }

  hideContextMenu() {
    if (this.contextMenu) this.contextMenu.style.display = 'none';
  }

  setupDataFetching() {
    if (this.amenityUrlValue) {
      this.debouncedLoad = MapUtils.debounce(this.drawHuntingData.bind(this), 500);
      this.map.on("moveend", this.debouncedLoad);
      this.drawHuntingData();
    }

    if (this.personalUrlValue) {
      this.debouncedLoadPersonal = MapUtils.debounce(this.drawPersonalData.bind(this), 500);
      this.map.on("moveend", this.debouncedLoadPersonal);
      this.drawPersonalData();
    }

    if (this.seenUrlValue) {
      this.debouncedLoadSeen = MapUtils.debounce(this.drawSeenData.bind(this), 500);
      this.map.on("moveend", this.debouncedLoadSeen);
      this.drawSeenData();
    }

    if (this.huntingAreasUrlValue) {
      this.debouncedLoadHuntingAreas = MapUtils.debounce(this.drawHuntingAreas.bind(this), 500);
      this.map.on("moveend", this.debouncedLoadHuntingAreas);
      this.drawHuntingAreas();
    }

    // Setup dropdown click handler
    this.setupDropdownHandler();

    // Auto-load killed game if checkbox is pre-checked (e.g. user is in active hunt)
    const killedGameToggle = document.getElementById("todayKilledGameToggle")
    if (killedGameToggle?.checked) {
      this.drawKilledGame()
    }
  }

  // Switch the active base layer (Teren / Karta / Satelit)
  switchBaseLayer(event) {
    const label = event.target.value;
    const layers = this.map && this.map.baseLayers;
    if (!layers || !layers[label]) return;

    if (this.map.activeBaseLayer) {
      this.map.removeLayer(this.map.activeBaseLayer);
    }
    layers[label].addTo(this.map);
    this.map.activeBaseLayer = layers[label];
    this.saveBaseLayerPreference(label);
  }

  async saveBaseLayerPreference(label) {
    localStorage.setItem('mapBaseLayer', label);
    try {
      await fetch('/map/save_base_layer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({ layer: label })
      });
    } catch (error) {
      console.error('Failed to save base layer preference:', error);
    }
  }

  // Toggle hunting areas layer visibility
  toggleHuntingAreas(event) {
    const isChecked = event.target.checked;
    
    if (this.huntingAreasRenderer) {
      if (isChecked) {
        this.huntingAreasRenderer.show();
      } else {
        this.huntingAreasRenderer.hide();
      }
    }

    // Save the state to session
    this.saveHuntingAreasToggleState(isChecked);
  }

  // Save hunting areas toggle state to session
  async saveHuntingAreasToggleState(visible) {
    try {
      await fetch('/map/toggle_hunting_areas', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({ visible: visible })
      });
    } catch (error) {
      console.error('Failed to save hunting areas toggle state:', error);
    }
  }

  // Toggle killed game layer visibility
  toggleKilledGame(event) {
    const isChecked = event.target.checked;
    
    if (isChecked) {
      // Load and show data
      this.drawKilledGame();
    } else {
      // Hide data
      if (this.killedGameRenderer) {
        this.killedGameRenderer.hide();
      }
    }
  }

  async drawKilledGame() {
    if (this.killedGameRenderer && this.killedGameUrlValue) {
      try {
        const response = await fetch(this.killedGameUrlValue);
        const data = await response.json();
        
        if (data && data.length > 0) {
          this.killedGameRenderer.render(data);
          this.killedGameRenderer.show();
        }
      } catch (error) {
        console.error('Failed to fetch killed game:', error);
      }
    }
  }

  async drawHuntingData() {
    const data = await this.dataFetcher.fetchHuntingData(this.amenityUrlValue);
    if (data) {
      this.renderer.renderData(data);
    }
  }

  async drawPersonalData() {
    const data = await this.dataFetcher.fetchPersonalData(this.personalUrlValue);
    if (data) {
      this.renderer.renderData(data);
    }
  }

  async drawSeenData() {
    const data = await this.dataFetcher.fetchSeenData(this.seenUrlValue);
    if (data) {
      this.renderer.renderData(data);
    }
  }

  async drawHuntingAreas() {
    if (this.huntingAreasRenderer) {
      await this.huntingAreasRenderer.loadAndRender(this.huntingAreasUrlValue);
      
      // Check the checkbox state and hide/show accordingly
      const checkbox = document.getElementById('huntingAreasToggle');
      if (checkbox && !checkbox.checked) {
        this.huntingAreasRenderer.hide();
      }
    }
  }

  // Setup dropdown click handler
  setupDropdownHandler() {
    setTimeout(() => {
      const dropdown = document.getElementById('mapAddDropdown');
      if (dropdown) { dropdown.addEventListener('click', this.updateDropdownLinks.bind(this)); }
    }, 500);
  }

  // Get current map center coordinates
  getCurrentMapCenter() {
    if (this.map) {
      const center = this.map.getCenter();
      return {
        latitude: center.lat,
        longitude: center.lng
      };
    }
    return null;
  }

  // Update dropdown links with current map center
  updateDropdownLinks() {
    const center = this.getCurrentMapCenter();
    if (!center) return;

    const dropdown = document.getElementById('mapAddDropdown');
    if (!dropdown) return;

    const dropdownMenu = dropdown.nextElementSibling;
    const links = dropdownMenu.querySelectorAll('a.dropdown-item');

    links.forEach(link => {
      const url = new URL(link.href);
      url.searchParams.set('latitude', center.latitude);
      url.searchParams.set('longitude', center.longitude);
      link.href = url.toString();
    });
  }
}
